/**
 * QA Panel — activation detection.
 *
 * Decides whether the panel should open at boot, and provides the
 * keyboard-shortcut listener for ad-hoc toggling. Three independent triggers:
 *
 *   1. URL query param  (?qa=1)        — easy to share via Slack
 *   2. localStorage     (qa-panel=1)   — persists across reloads on one machine
 *   3. Keyboard shortcut Ctrl/Cmd+Shift+Q — works any time, also counts as a
 *                                          user gesture for popup blockers
 */

import { testIds } from 'core/testIds';
import {
    ACTIVATION_KEY,
    ACTIVATION_STORAGE_KEY,
    ACTIVATION_URL_PARAM,
    LONG_PRESS_DURATION_MS,
    LONG_PRESS_MOVE_THRESHOLD_PX,
} from '../constants';
import { isCoarsePointer } from './container';
import { createLongPressIndicator } from './longPressIndicator';

export function shouldAutoOpenOnBoot(): boolean {
    if (typeof window === 'undefined') return false;

    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get(ACTIVATION_URL_PARAM) === '1') return true;
    } catch {
        // URLSearchParams unavailable or invalid query — ignore.
    }

    try {
        if (window.localStorage?.getItem(ACTIVATION_STORAGE_KEY) === '1') return true;
    } catch {
        // localStorage may throw in private mode / sandboxed iframes.
    }

    return false;
}

/**
 * Bind a global keyboard listener. Returns an unbind function.
 */
export function bindActivationShortcut(handler: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;

    const onKey = (event: KeyboardEvent) => {
        // Ctrl+Shift+Q on Win/Linux, Cmd+Shift+Q on macOS.
        const modifier = event.ctrlKey || event.metaKey;
        if (modifier && event.shiftKey && event.key.toUpperCase() === ACTIVATION_KEY) {
            event.preventDefault();
            handler();
        }
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
}

/**
 * Touch-device trigger: a 5-second long-press on the StatusBar settings button
 * opens the in-DOM overlay. Mirrors `bindActivationShortcut` — binds a GLOBAL
 * capture-phase listener on `window` rather than touching the button component,
 * so no navigation-feature internals are imported. The settings button is
 * matched by its `data-testid` (built from `core/testIds`, which is shared).
 *
 * Behavior:
 *   - On pointerdown over the settings button, start a 5s timer and render a
 *     transient progress ring over the button.
 *   - Cancel on pointerup / pointermove past threshold / scroll / contextmenu.
 *   - On completion, call `handler()` (the overlay needs no user gesture).
 *   - Suppress the settings button's normal click that would otherwise fire on
 *     release after a successful long-press, so the settings modal doesn't open.
 *   - Suppress the mobile long-press context menu / callout on the target.
 *
 * Desktop (fine pointer) keeps the keyboard shortcut and this is a no-op.
 * Returns an unbind function.
 */
export function bindActivationLongPress(handler: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    if (!isCoarsePointer()) return () => undefined;

    const selector = `[data-testid="${testIds.statusBar.settingsButton}"]`;
    const indicator = createLongPressIndicator();

    let timer: number | null = null;
    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    // Set after a completed long-press so the trailing click is swallowed.
    let suppressNextClick = false;

    const findButton = (target: EventTarget | null): Element | null => {
        if (!(target instanceof Element)) return null;
        return target.closest(selector);
    };

    const cancel = (): void => {
        if (timer !== null) {
            window.clearTimeout(timer);
            timer = null;
        }
        activePointerId = null;
        indicator.hide();
    };

    const onPointerDown = (event: PointerEvent): void => {
        // Only react to a single primary press starting on the settings button.
        if (timer !== null) return;
        const button = findButton(event.target);
        if (!button) return;

        activePointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        // Prevent the press from starting a text selection / native callout.
        event.preventDefault();
        indicator.show(button, LONG_PRESS_DURATION_MS);

        timer = window.setTimeout(() => {
            timer = null;
            activePointerId = null;
            suppressNextClick = true;
            indicator.hide();
            handler();
        }, LONG_PRESS_DURATION_MS);
    };

    const onPointerMove = (event: PointerEvent): void => {
        if (timer === null || event.pointerId !== activePointerId) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_THRESHOLD_PX) {
            cancel();
        }
    };

    const onPointerEnd = (event: PointerEvent): void => {
        if (event.pointerId !== activePointerId) return;
        cancel();
    };

    const onScroll = (): void => {
        cancel();
    };

    const onContextMenu = (event: Event): void => {
        // Block the mobile long-press context menu / callout on the button.
        if (findButton(event.target)) {
            event.preventDefault();
        }
    };

    const onClick = (event: Event): void => {
        if (!suppressNextClick) return;
        suppressNextClick = false;
        // Swallow the settings button's onClick (opens the settings modal) that
        // fires on release after a successful long-press.
        if (findButton(event.target)) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', onPointerEnd, true);
    window.addEventListener('pointercancel', onPointerEnd, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('click', onClick, true);

    return () => {
        cancel();
        indicator.destroy();
        window.removeEventListener('pointerdown', onPointerDown, true);
        window.removeEventListener('pointermove', onPointerMove, true);
        window.removeEventListener('pointerup', onPointerEnd, true);
        window.removeEventListener('pointercancel', onPointerEnd, true);
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('contextmenu', onContextMenu, true);
        window.removeEventListener('click', onClick, true);
    };
}
